import { BALANCE, NEED_KINDS, wearLevel, type ModuleState, type ShipState, type StationState } from "@station-keeping/sim";
import type * as Phaser from "phaser";
import { depthOf, toIso, type Point } from "./iso";
import { COLORS } from "./palette";
import { GLYPH_ADVANCE, MODULE_ANCHOR, MODULE_H, MODULE_W, WEAR_SEGMENTS, type SignKind } from "./sprites";
import { TEX, moduleSprite, type ArtBook } from "./textures";

type Image = Phaser.GameObjects.Image;
type Container = Phaser.GameObjects.Container;

const INK = 0x04060d;
/** Pendant la construction, les bâtiments s'effacent : on vise les cases de derrière. */
const FADED_ALPHA = 0.32;

/** Multiplie deux teintes (nuit × état du module). */
export function multiplyTint(a: number, b: number): number {
  const r = (((a >> 16) & 0xff) * ((b >> 16) & 0xff)) / 255;
  const g = (((a >> 8) & 0xff) * ((b >> 8) & 0xff)) / 255;
  const bl = ((a & 0xff) * (b & 0xff)) / 255;
  return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(bl);
}

/** Teinte d'un module selon son état : sombre en veille ou sans énergie, rosée quand la panne l'arrête. */
function stateTint(module: ModuleState): number {
  if (module.failure?.stage === 3) return 0xc79aa6;
  if (module.status === "off" || module.status === "unpowered" || module.status === "unstaffed") return 0x8a93b8;
  return 0xffffff;
}

function signOf(module: ModuleState): SignKind | null {
  if (module.failure) return module.failure.stage === 1 ? "stage1" : module.failure.stage === 2 ? "stage2" : "stage3";
  if (module.job) return "job";
  if (module.status === "off" || module.status === "unpowered" || module.status === "unstaffed") return module.status;
  return null;
}

/** Jauge horizontale en pixels : un fond sombre et un remplissage teinté. */
class PixelBar {
  private readonly back: Image;
  private readonly fill: Image;

  constructor(
    scene: Phaser.Scene,
    layer: Container,
    private readonly width: number,
  ) {
    this.back = scene.add.image(0, 0, TEX.pixel).setOrigin(0, 0).setTint(INK);
    this.fill = scene.add.image(0, 0, TEX.pixel).setOrigin(0, 0);
    layer.add([this.back, this.fill]);
    this.hide();
  }

  show(x: number, y: number, ratio: number, color: number, depth: number): void {
    const value = Math.min(1, Math.max(0, ratio));
    this.back.setPosition(x - 2, y - 2).setScale(this.width + 4, 8).setDepth(depth).setVisible(true);
    this.fill
      .setPosition(x, y)
      .setScale(Math.max(1, Math.round(this.width * value)), 4)
      .setTint(color)
      .setDepth(depth + 0.1)
      .setVisible(true);
  }

  hide(): void {
    this.back.setVisible(false);
    this.fill.setVisible(false);
  }

  destroy(): void {
    this.back.destroy();
    this.fill.destroy();
  }
}

interface Puff {
  image: Image;
  born: number;
  x: number;
  y: number;
}

/**
 * Un module : son bloc (dans le monde, trié par profondeur), ses lumières de nuit, l'échafaudage d'un
 * chantier, et sa plaquette d'usure posée sur lui (cachée par ce qui est devant, comme lui) ; sur le calque
 * supérieur, au-dessus de lui, la jauge d'un chantier ou d'une intervention, son panneau d'état et sa fumée.
 */
export class ModuleView {
  private readonly body: Image;
  private lights: Image | null = null;
  private readonly scaffold: Image;
  private readonly sign: Image;
  private readonly progress: PixelBar;
  private readonly wear: Image;
  private readonly center: Point;
  private height: number;
  private readonly depth: number;
  /** Niveau d'amélioration dessiné : son apparence change avec lui. */
  private level: number;
  private signKind: SignKind | null = null;
  private tint = -1;
  private puffs: Puff[] = [];
  private lastPuff = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly world: Container,
    private readonly overlay: Container,
    module: ModuleState,
    private readonly book: ArtBook,
  ) {
    this.center = toIso(module.x, module.y);
    this.depth = depthOf(module.x, module.y);
    this.level = module.level;
    this.height = moduleSprite(book, module.type, module.level).height;
    const originX = MODULE_ANCHOR.x / MODULE_W;
    const originY = MODULE_ANCHOR.y / MODULE_H;
    this.body = scene.add
      .image(this.center.x, this.center.y, TEX.module(module.type, module.level))
      .setOrigin(originX, originY)
      .setDepth(this.depth);
    this.scaffold = scene.add
      .image(this.center.x, this.center.y, TEX.scaffold)
      .setOrigin(originX, originY)
      .setDepth(this.depth + 0.2)
      .setVisible(false);
    world.add([this.body, this.scaffold]);
    this.syncLights(module);
    this.sign = scene.add.image(0, 0, TEX.sign("stage1")).setVisible(false);
    overlay.add(this.sign);
    this.progress = new PixelBar(scene, overlay, 32);
    // Dans le monde, juste devant le module : la plaquette reste sur lui, jamais sur un voisin.
    this.wear = scene.add
      .image(this.center.x, this.center.y - 8, TEX.wear("worn", 0))
      .setDepth(this.depth + 0.3)
      .setVisible(false);
    world.add(this.wear);
  }

  /** Lumières de nuit du niveau dessiné : un module amélioré peut en gagner. */
  private syncLights(module: ModuleState): void {
    const sprite = moduleSprite(this.book, module.type, this.level);
    if (!sprite.hasLights) {
      this.lights?.destroy();
      this.lights = null;
      return;
    }
    const key = TEX.lights(module.type, this.level);
    if (this.lights) {
      this.lights.setTexture(key);
      return;
    }
    this.lights = this.scene.add
      .image(this.center.x, this.center.y, key)
      .setOrigin(MODULE_ANCHOR.x / MODULE_W, MODULE_ANCHOR.y / MODULE_H)
      .setDepth(this.depth + 0.1);
    this.world.add(this.lights);
  }

  update(
    module: ModuleState,
    state: StationState,
    alpha: number,
    time: number,
    nightTint: number,
    night: number,
    faded: boolean,
  ): void {
    const building = module.buildLeft > 0;
    if (module.level !== this.level) {
      this.level = module.level;
      this.body.setTexture(TEX.module(module.type, module.level));
      this.height = moduleSprite(this.book, module.type, module.level).height;
      this.syncLights(module);
    }
    const tint = multiplyTint(nightTint, stateTint(module));
    if (tint !== this.tint) {
      this.tint = tint;
      this.body.setTint(tint);
      this.scaffold.setTint(nightTint);
    }
    const fade = faded ? FADED_ALPHA : 1;
    this.body.setAlpha((building ? 0.4 : 1) * fade);
    this.scaffold.setVisible(building).setAlpha(fade);
    if (this.lights) this.lights.setVisible(!building && module.status !== "off").setAlpha((0.18 + 0.82 * night) * fade);

    const top = this.center.y - this.height;

    // Au-dessus du module, l'avancement d'un chantier ou d'une intervention.
    if (building) {
      const total = BALANCE.modules[module.type].buildSeconds;
      this.progress.show(this.center.x - 16, top - 6, 1 - (module.buildLeft - alpha) / total, COLORS.accent, this.depth);
    } else if (module.job) {
      const total = Math.max(1, module.job.endsAt - module.job.startedAt);
      const left = module.job.endsAt - state.tick - alpha;
      const color = module.job.kind === "patch" ? COLORS.warn : module.job.kind === "upgrade" ? COLORS.accent : COLORS.good;
      this.progress.show(this.center.x - 16, top - 6, 1 - left / total, color, this.depth);
    } else {
      this.progress.hide();
    }

    // Sur le module, l'usure quand elle mérite l'attention (tramage d'autant plus dense qu'il est fatigué).
    // Un vaisseau à quai occupe le poste : la plaquette s'efface, rien ne se lit avec ses besoins.
    const level = wearLevel(module.wear);
    const berthed = state.ships.some((ship) => ship.berth === module.id && ship.phase === "docked");
    if (!building && !module.job && !berthed && module.type !== "core" && level !== "ok") {
      const filled = Math.max(1, Math.round((module.wear / BALANCE.wear.max) * WEAR_SEGMENTS));
      this.wear
        .setTexture(TEX.wear(level === "critical" ? "critical" : "worn", Math.min(WEAR_SEGMENTS, filled)))
        .setAlpha(faded ? FADED_ALPHA : 1)
        .setVisible(true);
    } else {
      this.wear.setVisible(false);
    }

    // Panneau d'état au-dessus du module, qui flotte doucement.
    const kind = signOf(module);
    if (kind !== this.signKind) {
      this.signKind = kind;
      if (kind) this.sign.setTexture(TEX.sign(kind));
    }
    if (kind) {
      const bob = Math.round(Math.sin(time / 260 + this.center.x) * 2);
      const blink = kind === "stage3" && Math.floor(time / 400) % 2 === 1 ? 0.55 : 1;
      this.sign.setPosition(this.center.x, top - 18 + bob).setDepth(this.depth + 0.5).setAlpha(blink).setVisible(true);
    } else {
      this.sign.setVisible(false);
    }

    // Fumée d'une panne aggravée.
    if (module.failure && module.failure.stage >= 2 && time - this.lastPuff > (module.failure.stage === 3 ? 420 : 700)) {
      this.lastPuff = time;
      const x = this.center.x + (Math.random() * 20 - 10);
      const image = this.scene.add.image(x, top + 8, TEX.puff).setDepth(this.depth + 0.3);
      this.overlay.add(image);
      this.puffs.push({ image, born: time, x, y: top + 8 });
    }
    this.puffs = this.puffs.filter((puff) => {
      const age = (time - puff.born) / 1500;
      if (age >= 1) {
        puff.image.destroy();
        return false;
      }
      puff.image
        .setPosition(puff.x + age * 6, puff.y - age * 28)
        .setScale(1 + age * 0.8)
        .setAlpha(0.75 * (1 - age));
      return true;
    });
  }

  destroy(): void {
    this.body.destroy();
    this.scaffold.destroy();
    this.lights?.destroy();
    this.sign.destroy();
    this.progress.destroy();
    this.wear.destroy();
    for (const puff of this.puffs) puff.image.destroy();
  }
}

/**
 * L'autoroute spatiale : une voie droite qui passe derrière la station (en haut de l'écran), de gauche à
 * droite, dans le repère de la grille. Sur elle, `s = x − y` avance vers la droite de l'écran ; `x + y` la
 * situe en profondeur, une rangée derrière le dernier anneau ouvert.
 */
function laneSum(rings: number): number {
  return -(2 * rings + 2);
}

/** Étendue de la voie en `s` : bien au-delà de ce que la vue montre, pour entrer et sortir hors champ. */
function laneReach(rings: number): number {
  return 2 * rings + 13;
}

/** Point de la voie à l'abscisse `s`, décalé de `offset` en profondeur (les vaisseaux ne roulent pas en file indienne). */
function lanePoint(rings: number, s: number, offset = 0): Point {
  const sum = laneSum(rings) + offset;
  return { x: (sum + s) / 2, y: (sum - s) / 2 };
}

function lerpPoint(a: Point, b: Point, t: number): Point {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Décalage d'un vaisseau dans la voie, stable pour un identifiant donné. */
function laneOffset(id: string): number {
  const n = Number(id.replace(/\D/g, "")) || 0;
  return (((n * 37) % 7) - 3) * 0.09;
}

/** Altitude des vaisseaux au-dessus du pont (pixels d'art) : ils roulent haut sur l'autoroute, puis se posent à quai. */
const ALTITUDE = { highway: 30, approach: 22, docked: 12, leaving: 30 } as const;

/** Poste réservé d'un vaisseau, retenu pour le retour vers l'autoroute (le départ efface le poste). */
interface BerthSpot {
  x: number;
  y: number;
  core: boolean;
}

/** Où se pose un vaisseau : à côté de son dock, vers l'extérieur ; au sas du noyau, du côté de l'autoroute. */
function dockPoint(berth: BerthSpot): Point {
  if (berth.core) return { x: berth.x - 0.55, y: berth.y - 0.55 };
  const length = Math.hypot(berth.x, berth.y) || 1;
  return { x: berth.x + (berth.x / length) * 0.62, y: berth.y + (berth.y / length) * 0.62 };
}

/**
 * Un vaisseau appelé par la station : il entre sur l'autoroute hors champ, la quitte au-dessus de son poste,
 * descend s'y amarrer, puis remonte sur la voie et repart vers la droite.
 */
export class ShipView {
  private readonly body: Image;
  private readonly lights: Image;
  private readonly flame: Image;
  private readonly bracket: Image;
  private readonly dots: Image[] = [];
  private gx: number;
  private gy: number;
  private altitude: number;
  private x = 0;
  private y = 0;
  private screenX = Number.NaN;
  private facingLeft = false;
  private dotsKey = "";
  private tint = -1;
  private shipClass: ShipState["class"];
  private berth: BerthSpot | null = null;
  /** Où le vaisseau a commencé à repartir. */
  private leaveFrom: Point | null = null;
  private readonly offset: number;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly world: Container,
    ship: ShipState,
    berths: Map<string, ModuleState>,
    private readonly book: ArtBook,
    rings: number,
  ) {
    this.shipClass = ship.class;
    this.offset = laneOffset(ship.id);
    this.rememberBerth(ship, berths);
    // Un vaisseau déjà à quai (reprise de partie) s'y montre ; les autres arrivent par l'autoroute.
    const start =
      ship.phase === "docked" && this.berth ? dockPoint(this.berth) : lanePoint(rings, -laneReach(rings), this.offset);
    this.gx = start.x;
    this.gy = start.y;
    this.altitude = ALTITUDE[ship.phase];
    this.body = scene.add.image(0, 0, TEX.ship(ship.class));
    this.lights = scene.add.image(0, 0, TEX.shipLights(ship.class));
    this.flame = scene.add.image(0, 0, TEX.flame(0)).setVisible(false);
    this.bracket = scene.add.image(0, 0, TEX.bracket).setVisible(false);
    world.add([this.flame, this.body, this.lights, this.bracket]);
  }

  private rememberBerth(ship: ShipState, berths: Map<string, ModuleState>): void {
    const berth = ship.berth ? berths.get(ship.berth) : undefined;
    if (berth) this.berth = { x: berth.x, y: berth.y, core: berth.type === "core" };
  }

  /** Position visée selon la phase, dans le repère de la grille. */
  private target(ship: ShipState, alpha: number, rings: number, tick: number): Point {
    const reach = laneReach(rings);
    const exitS = this.berth ? this.berth.x - this.berth.y : this.gx - this.gy;
    switch (ship.phase) {
      case "highway": {
        // Entré hors champ à l'appel, il atteint la sortie au-dessus de son poste quand la phase s'achève.
        const remaining = Math.max(0, ship.phaseLeft - alpha);
        const elapsed = Math.max(0, tick - ship.arrivedAt + alpha);
        const progress = elapsed + remaining > 0 ? elapsed / (elapsed + remaining) : 1;
        return lanePoint(rings, -reach + (exitS + reach) * progress, this.offset);
      }
      case "approach": {
        const progress = clamp01(1 - (ship.phaseLeft - alpha) / BALANCE.ships.approachSeconds);
        const exit = lanePoint(rings, exitS, this.offset);
        return this.berth ? lerpPoint(exit, dockPoint(this.berth), progress) : exit;
      }
      case "docked":
        return this.berth ? dockPoint(this.berth) : { x: this.gx, y: this.gy };
      case "leaving": {
        this.leaveFrom ??= { x: this.gx, y: this.gy };
        const progress = clamp01(1 - (ship.phaseLeft - alpha) / BALANCE.ships.leaveSeconds);
        const from = this.leaveFrom.x - this.leaveFrom.y;
        // Il remonte sur la voie, puis file vers la droite et sort du champ.
        if (progress < 0.4) return lerpPoint(this.leaveFrom, lanePoint(rings, from + 0.5, this.offset), progress / 0.4);
        return lanePoint(rings, from + 0.5 + (reach - from) * ((progress - 0.4) / 0.6), this.offset);
      }
    }
  }

  /** Le point du monde touche-t-il le vaisseau (silhouette au pixel près, marge de quelques pixels) ? */
  contains(point: Point): boolean {
    const info = this.book.ships[this.shipClass];
    let lx = Math.floor(point.x - this.x + info.width / 2);
    const ly = Math.floor(point.y - this.y + info.height / 2);
    if (this.facingLeft) lx = info.width - 1 - lx;
    for (let dy = -4; dy <= 4; dy += 2) {
      for (let dx = -4; dx <= 4; dx += 2) if (info.mask.alphaAt(lx + dx, ly + dy) > 0) return true;
    }
    return false;
  }

  get depth(): number {
    return depthOf(this.gx, this.gy) + 60;
  }

  update(
    ship: ShipState,
    berths: Map<string, ModuleState>,
    alpha: number,
    tick: number,
    time: number,
    deltaMs: number,
    rings: number,
    nightTint: number,
    night: number,
    selected: boolean,
  ): void {
    if (ship.phase !== "leaving") this.rememberBerth(ship, berths);
    const goal = this.target(ship, alpha, rings, tick);
    // Sur l'autoroute et en approche, le vaisseau suit sa trajectoire de près ; à quai et au départ, il glisse.
    const lag = ship.phase === "highway" || ship.phase === "approach" ? 60 : 220;
    const smoothing = 1 - Math.exp(-deltaMs / lag);
    this.gx += (goal.x - this.gx) * smoothing;
    this.gy += (goal.y - this.gy) * smoothing;
    this.altitude += (ALTITUDE[ship.phase] - this.altitude) * smoothing;

    const ground = toIso(this.gx, this.gy);
    this.x = ground.x;
    this.y = ground.y - this.altitude;
    if (!Number.isNaN(this.screenX) && Math.abs(this.x - this.screenX) > 0.04) this.facingLeft = this.x < this.screenX;
    this.screenX = this.x;

    const depth = this.depth;
    const leaving = ship.phase === "leaving" ? clamp01(1 - (ship.phaseLeft - alpha) / BALANCE.ships.leaveSeconds) : 0;
    const fade = leaving > 0.85 ? (1 - leaving) / 0.15 : 1;
    this.body.setPosition(this.x, this.y).setFlipX(this.facingLeft).setDepth(depth).setAlpha(fade);
    this.lights
      .setPosition(this.x, this.y)
      .setFlipX(this.facingLeft)
      .setDepth(depth + 0.1)
      .setAlpha(fade * (0.2 + 0.8 * night));
    if (nightTint !== this.tint) {
      this.tint = nightTint;
      this.body.setTint(nightTint);
    }

    // Coins de sélection autour du vaisseau choisi, qui respirent.
    const info = this.book.ships[ship.class];
    if (selected) {
      const pulse = 1 + Math.sin(time / 260) * 0.04;
      this.bracket
        .setPosition(this.x, this.y)
        .setScale(((info.width + 16) / 72) * pulse, ((info.height + 14) / 44) * pulse)
        .setDepth(depth + 0.3)
        .setVisible(true);
    } else {
      this.bracket.setVisible(false);
    }

    // Flamme de tuyère tant qu'il vole.
    if (ship.phase !== "docked") {
      const frame = Math.floor(time / 90) % 2 === 0 ? 0 : 1;
      const side = this.facingLeft ? 1 : -1;
      this.flame
        .setTexture(TEX.flame(frame))
        .setPosition(this.x + side * (info.width / 2 - info.nozzle.x + 6), this.y + (info.nozzle.y - info.height / 2))
        .setFlipX(this.facingLeft)
        .setDepth(depth - 0.1)
        .setAlpha(fade)
        .setVisible(true);
    } else {
      this.flame.setVisible(false);
    }

    // Pastilles des besoins encore à servir, une fois sorti de l'autoroute.
    const shown = ship.phase === "approach" || ship.phase === "docked";
    const missing = shown ? NEED_KINDS.filter((kind) => ship.served[kind] < ship.asked[kind]) : [];
    const key = missing.join(",");
    if (key !== this.dotsKey) {
      this.dotsKey = key;
      for (const dot of this.dots) dot.destroy();
      this.dots.length = 0;
      for (const kind of missing) {
        const dot = this.scene.add.image(0, 0, TEX.dot(kind));
        this.world.add(dot);
        this.dots.push(dot);
      }
    }
    this.dots.forEach((dot, index) => {
      const offset = (index - (this.dots.length - 1) / 2) * 15;
      dot
        .setPosition(Math.round(this.x + offset), Math.round(this.y - info.height / 2 - 12))
        .setDepth(depth + 0.2)
        .setAlpha(fade);
    });
  }

  destroy(): void {
    this.body.destroy();
    this.lights.destroy();
    this.flame.destroy();
    this.bracket.destroy();
    for (const dot of this.dots) dot.destroy();
  }
}

/** Le trafic de passage, toujours là : plus dense quand la station a bonne réputation. */
const TRAFFIC_MIN = 4;
const TRAFFIC_MAX = 10;
/** Classes du trafic de passage, dans l'ordre où elles défilent. */
const TRAFFIC_CLASSES: readonly ShipState["class"][] = ["shuttle", "freighter", "shuttle", "liner", "freighter", "shuttle", "liner", "shuttle", "freighter", "liner"];
/** Écart entre deux balises de la voie, en `s`. */
const BEACON_STEP = 1.5;

interface PassingShip {
  body: Image;
  lights: Image;
  flame: Image;
  shipClass: ShipState["class"];
  /** Vitesse en `s` par seconde, et décalage de départ. */
  speed: number;
  phase: number;
  offset: number;
}

/**
 * L'autoroute elle-même : deux rangées de balises qui marquent la voie (une onde lumineuse les parcourt,
 * immobile si l'on réduit les animations) et les vaisseaux qui passent sans s'arrêter.
 */
export class HighwayView {
  private readonly passing: PassingShip[] = [];
  private readonly beacons: Image[] = [];
  private readonly reducedMotion: boolean;
  private tint = -1;

  constructor(
    scene: Phaser.Scene,
    world: Container,
    private readonly book: ArtBook,
  ) {
    this.reducedMotion = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    TRAFFIC_CLASSES.forEach((shipClass, index) => {
      const body = scene.add.image(0, 0, TEX.ship(shipClass)).setVisible(false);
      const lights = scene.add.image(0, 0, TEX.shipLights(shipClass)).setVisible(false);
      const flame = scene.add.image(0, 0, TEX.flame(0)).setVisible(false);
      world.add([flame, body, lights]);
      this.passing.push({
        body,
        lights,
        flame,
        shipClass,
        speed: 2.2 + ((index * 7) % 5) * 0.35,
        phase: index * 9.7,
        offset: (((index * 5) % 7) - 3) * 0.12,
      });
    });
    // Assez de balises pour le plus grand des anneaux, sur deux rangées.
    const count = Math.ceil((2 * laneReach(BALANCE.grid.maxRing)) / BEACON_STEP) + 1;
    for (let i = 0; i < count * 2; i++) {
      const beacon = scene.add.image(0, 0, TEX.pixel).setScale(6, 3).setVisible(false);
      world.add(beacon);
      this.beacons.push(beacon);
    }
  }

  update(rings: number, reputation: number, time: number, nightTint: number, night: number): void {
    const reach = laneReach(rings);
    const span = 2 * reach;

    // Balises : deux rangées de part et d'autre de la voie, sous les vaisseaux.
    const perRow = Math.ceil(span / BEACON_STEP) + 1;
    const wave = this.reducedMotion ? -1 : Math.floor(time / 140);
    this.beacons.forEach((beacon, index) => {
      const row = index % 2;
      const i = Math.floor(index / 2);
      if (i >= perRow) {
        beacon.setVisible(false);
        return;
      }
      const s = -reach + i * BEACON_STEP;
      const point = lanePoint(rings, s, row === 0 ? -0.7 : 0.7);
      const ground = toIso(point.x, point.y);
      const lit = wave >= 0 && (wave - i) % 10 === 0;
      beacon
        .setPosition(Math.round(ground.x), Math.round(ground.y - ALTITUDE.highway + 12))
        .setTint(lit ? COLORS.accent : 0x3f6fb0)
        .setAlpha(lit ? 1 : 0.7 + 0.3 * night)
        .setDepth(depthOf(point.x, point.y) + 50)
        .setVisible(true);
    });

    // Vaisseaux de passage.
    const count = TRAFFIC_MIN + Math.round(((TRAFFIC_MAX - TRAFFIC_MIN) * reputation) / 10_000);
    if (nightTint !== this.tint) {
      this.tint = nightTint;
      for (const ship of this.passing) ship.body.setTint(nightTint);
    }
    this.passing.forEach((ship, index) => {
      if (index >= count) {
        ship.body.setVisible(false);
        ship.lights.setVisible(false);
        ship.flame.setVisible(false);
        return;
      }
      const travelled = (((time / 1000) * ship.speed + ship.phase) % span + span) % span;
      const s = -reach + travelled;
      const point = lanePoint(rings, s, ship.offset);
      const ground = toIso(point.x, point.y);
      const x = Math.round(ground.x);
      const y = Math.round(ground.y - ALTITUDE.highway);
      // Il s'efface aux deux bouts de la voie.
      const edge = Math.min(travelled, span - travelled);
      const fade = clamp01(edge / 2);
      const depth = depthOf(point.x, point.y) + 60;
      const info = this.book.ships[ship.shipClass];
      ship.body.setPosition(x, y).setDepth(depth).setAlpha(fade).setVisible(true);
      ship.lights.setPosition(x, y).setDepth(depth + 0.1).setAlpha(fade * (0.2 + 0.8 * night)).setVisible(true);
      const frame = Math.floor(time / 90 + index) % 2 === 0 ? 0 : 1;
      ship.flame
        .setTexture(TEX.flame(frame))
        .setPosition(x - (info.width / 2 - info.nozzle.x + 6), y + (info.nozzle.y - info.height / 2))
        .setDepth(depth - 0.1)
        .setAlpha(fade)
        .setVisible(true);
    });
  }
}

/** Texte en police pixel (gains), qui monte et s'efface. */
export class PixelLabel {
  private readonly container: Container;

  constructor(
    scene: Phaser.Scene,
    layer: Container,
    text: string,
    private readonly x: number,
    private readonly y: number,
    private readonly born: number,
  ) {
    this.container = scene.add.container(x, y);
    const advance = (char: string) => (char === " " ? 4 : GLYPH_ADVANCE);
    const width = [...text].reduce((sum, char) => sum + advance(char), 0);
    let cursor = -Math.round(width / 2);
    for (const char of text) {
      if (char !== " ") this.container.add(scene.add.image(cursor, 0, TEX.font, char).setOrigin(0, 0.5));
      cursor += advance(char);
    }
    this.container.setDepth(1e9);
    layer.add(this.container);
  }

  /** Faux une fois le texte effacé (et détruit). */
  update(now: number, lifeMs: number): boolean {
    const age = (now - this.born) / lifeMs;
    if (age >= 1) {
      this.container.destroy();
      return false;
    }
    this.container.setPosition(this.x, Math.round(this.y - age * 32)).setAlpha(1 - age * age);
    return true;
  }

  destroy(): void {
    this.container.destroy();
  }
}
